/**
 * Stripe Configuration for Exclusive Offers
 *
 * This file contains all Stripe Product and Price IDs for the exclusive offers system.
 *
 * IMPORTANT:
 * - TEST mode IDs are used by default
 * - PROD mode IDs must be created in Stripe Dashboard when ready for production
 * - Update PROD IDs before deploying to production
 * - See EXCLUSIVE_OFFERS_PROD.md for the complete procedure
 */

const STRIPE_MODE = process.env.VUE_APP_STRIPE_MODE || 'test'; // 'test' or 'live'

/**
 * Exclusive Offers Configuration
 * Each offer has:
 * - productId: Stripe Product ID
 * - priceId: Stripe Price ID
 * - type: 'subscription' (recurring) or 'payment' (one-time)
 * - stripeRole: The role to assign in Firestore after payment ('solo' or 'multi')
 */
const EXCLUSIVE_OFFERS_CONFIG = {
  test: {
    'solo-discount': {
      productId: 'prod_K6ey30OLhfnmqx',
      priceId: 'price_1SL2lQKI8K6qvLcDtkw2Iqzx',
      type: 'subscription',
      stripeRole: 'solo',
      name: 'Solo Kid -50%',
      discount: 50
    },
    'multi-discount': {
      productId: 'prod_K6eyLDVX3zKXnA',
      priceId: 'price_1SL2kNKI8K6qvLcDsLRWS0J8',
      type: 'subscription',
      stripeRole: 'multi',
      name: 'Multi Kids -50%',
      discount: 50
    },
    'solo-lifetime': {
      productId: 'prod_THc1fNgJ2bQFQe',
      priceId: 'price_1SL2nCKI8K6qvLcDLuvYNKhR',
      type: 'payment',
      stripeRole: 'solo',
      name: 'Solo Kid Lifetime',
      isLifetime: true
    },
    'multi-lifetime': {
      productId: 'prod_THc27hmDMkMqYy',
      priceId: 'price_1SL2opKI8K6qvLcDs4EmNdDx',
      type: 'payment',
      stripeRole: 'multi',
      name: 'Multi Kids Lifetime',
      isLifetime: true
    }
  },
  live: {
    'solo-discount': {
      productId: 'prod_K2aopnqeSojkeX', 
      priceId: 'price_1SL26FKI8K6qvLcDwFml3u5p',   
      type: 'subscription',
      stripeRole: 'solo',
      name: 'Solo Kid -50%',
      discount: 50
    },
    'multi-discount': {
      productId: 'prod_K2aoL1dxkyl5nw',
      priceId: 'price_1SL24nKI8K6qvLcDJRGqt7eV',  
      type: 'subscription',
      stripeRole: 'multi',
      name: 'Multi Kids -50%',
      discount: 50
    },
    'solo-lifetime': {
      productId: 'prod_THbMqvfAv181gQ',
      priceId: 'price_1SL29wKI8K6qvLcDPX64fO7z', 
      type: 'payment',
      stripeRole: 'solo',
      name: 'Solo Kid Lifetime',
      isLifetime: true
    },
    'multi-lifetime': {
      productId: 'prod_THbNdQ6sxAYUoL',
      priceId: 'price_1SL2B2KI8K6qvLcDpDuCkRkB',
      type: 'payment',
      stripeRole: 'multi',
      name: 'Multi Kids Lifetime',
      isLifetime: true
    }
  }
};

/**
 * Get the current configuration based on STRIPE_MODE
 */
export function getExclusiveOffersConfig() {
  const config = EXCLUSIVE_OFFERS_CONFIG[STRIPE_MODE];

  if (!config) {
    console.error(`Invalid STRIPE_MODE: ${STRIPE_MODE}. Falling back to 'test'.`);
    return EXCLUSIVE_OFFERS_CONFIG.test;
  }

  return config;
}

/**
 * Get configuration for a specific offer
 * @param {string} offerKey - The offer key ('solo-discount', 'multi-discount', etc.)
 */
export function getOfferConfig(offerKey) {
  const config = getExclusiveOffersConfig();
  return config[offerKey];
}

/**
 * Get all available offer keys
 */
export function getAvailableOffers() {
  return Object.keys(getExclusiveOffersConfig());
}

/**
 * Check if we're in test mode
 */
export function isTestMode() {
  return STRIPE_MODE === 'test';
}

/**
 * Get Stripe publishable key based on mode
 */
export function getStripePublishableKey() {
  return STRIPE_MODE === 'test'
    ? process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY_TEST
    : process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY_LIVE;
}

export default {
  getExclusiveOffersConfig,
  getOfferConfig,
  getAvailableOffers,
  isTestMode,
  getStripePublishableKey,
  STRIPE_MODE
};
